import { Link, router } from '@inertiajs/react'
import { useState } from 'react'

import AdminLayout from '../../../components/AdminLayout'
import { AdminHead } from '../../../components/admin/AdminHead'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'

interface OptionForm {
  key: string
  label: string
  labelRu: string
  description: string
  descriptionRu: string
  priceDelta: string
  available: boolean
  imageUrl: string | null
  imageFile?: File
  removeImage?: boolean
}

interface StepForm {
  key: string
  kind: 'selection' | 'engraving'
  title: string
  titleRu: string
  label: string
  labelRu: string
  hint: string
  hintRu: string
  required: boolean
  charLimit: string
  placeholder: string
  imageUrl: string | null
  imageFile?: File
  removeImage?: boolean
  options: OptionForm[]
}

interface Props {
  product: { id: number; name: string; price: number; customizable: boolean }
  steps: StepForm[]
  errors?: Record<string, string>
}

/** "Pin colour" → "pin_colour", so the operator rarely types a key. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40)
}

const blankOption = (): OptionForm => ({
  key: '',
  label: '',
  labelRu: '',
  description: '',
  descriptionRu: '',
  priceDelta: '0',
  available: true,
  imageUrl: null,
})

const blankStep = (kind: 'selection' | 'engraving'): StepForm => ({
  key: '',
  kind,
  title: '',
  titleRu: '',
  label: '',
  labelRu: '',
  hint: '',
  hintRu: '',
  required: kind === 'selection',
  charLimit: kind === 'engraving' ? '16' : '',
  placeholder: '',
  imageUrl: null,
  options: kind === 'selection' ? [blankOption()] : [],
})

/**
 * The configurator editor: ordered steps, their copy and image, and the
 * options behind each selection step with the price bump each carries.
 *
 * The whole thing posts at once and the server reconciles by key, so
 * reordering, renaming and deleting are one round trip. Keys are what
 * past orders reference, so they're editable but flagged as permanent.
 */
export default function AdminProductsCustomization({ product, steps: initial, errors }: Props) {
  // Nullable columns arrive as null; FormData would stringify those to
  // "null" and the server would reject the save.
  const [steps, setSteps] = useState<StepForm[]>(() =>
    initial.map((step) => ({
      ...step,
      title: step.title ?? '',
      titleRu: step.titleRu ?? '',
      label: step.label ?? '',
      labelRu: step.labelRu ?? '',
      hint: step.hint ?? '',
      hintRu: step.hintRu ?? '',
      charLimit: step.charLimit == null ? '' : String(step.charLimit),
      placeholder: step.placeholder ?? '',
      options: step.options.map((option) => ({
        ...option,
        label: option.label ?? '',
        labelRu: option.labelRu ?? '',
        description: option.description ?? '',
        descriptionRu: option.descriptionRu ?? '',
        priceDelta: option.priceDelta == null ? '0' : String(option.priceDelta),
      })),
    }))
  )
  const [saving, setSaving] = useState(false)

  const patch = (index: number, changes: Partial<StepForm>) =>
    setSteps((current) => current.map((step, i) => (i === index ? { ...step, ...changes } : step)))

  const patchOption = (stepIndex: number, optionIndex: number, changes: Partial<OptionForm>) =>
    setSteps((current) =>
      current.map((step, i) =>
        i === stepIndex
          ? {
              ...step,
              options: step.options.map((option, j) =>
                j === optionIndex ? { ...option, ...changes } : option
              ),
            }
          : step
      )
    )

  const move = (index: number, delta: number) =>
    setSteps((current) => {
      const next = [...current]
      const target = index + delta
      if (target < 0 || target >= next.length) return current
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)

    // Multipart, because steps and options can each carry an image.
    const form = new FormData()
    form.append('_method', 'patch')
    // Lets the server tell "operator removed every step" apart from
    // "the payload didn't parse", so a bad request can't wipe the
    // configurator.
    form.append('steps_count', String(steps.length))

    steps.forEach((step, i) => {
      const key = step.key || slugify(step.label || step.title)
      const at = (field: string) => `steps[${i}][${field}]`
      form.append(at('key'), key)
      form.append(at('kind'), step.kind)
      form.append(at('title'), step.title)
      form.append(at('titleRu'), step.titleRu)
      form.append(at('label'), step.label)
      form.append(at('labelRu'), step.labelRu)
      form.append(at('hint'), step.hint)
      form.append(at('hintRu'), step.hintRu)
      form.append(at('required'), String(step.required))
      form.append(at('charLimit'), step.charLimit)
      form.append(at('placeholder'), step.placeholder)
      form.append(at('removeImage'), String(!!step.removeImage))
      if (step.imageFile) form.append(`step_images[${key}]`, step.imageFile)

      step.options.forEach((option, j) => {
        const optionKey = option.key || slugify(option.label)
        const on = (field: string) => `steps[${i}][options][${j}][${field}]`
        form.append(on('key'), optionKey)
        form.append(on('label'), option.label)
        form.append(on('labelRu'), option.labelRu)
        form.append(on('description'), option.description)
        form.append(on('descriptionRu'), option.descriptionRu)
        form.append(on('priceDelta'), option.priceDelta || '0')
        form.append(on('available'), String(option.available))
        form.append(on('removeImage'), String(!!option.removeImage))
        if (option.imageFile) form.append(`option_images[${key}.${optionKey}]`, option.imageFile)
      })
    })

    router.post(`/admin/products/${product.id}/customization`, form, {
      forceFormData: true,
      onFinish: () => setSaving(false),
    })
  }

  return (
    <AdminLayout>
      <AdminHead
        eyebrow="Catalog"
        title="Configurator"
        sub={`${product.name} · base $${product.price.toFixed(2)}`}
        back={{ href: `/admin/products/${product.id}/edit`, label: 'Back to product' }}
        actions={
          <>
            <Button asChild variant="outline" size="compact">
              <a href={`/customize/${product.id}`} target="_blank" rel="noreferrer">
                Preview →
              </a>
            </Button>
            <Button variant="primary" size="compact" onClick={submit} processing={saving}>
              Save configurator
            </Button>
          </>
        }
      />

      {!product.customizable && (
        <div className="mb-4 rounded-md border border-signal bg-signal-tint px-4 py-3 text-[12px] text-signal-deep">
          This product isn’t marked as made to order, so these steps won’t be shown.{' '}
          <Link href={`/admin/products/${product.id}/edit`} className="underline underline-offset-4">
            Turn customization on
          </Link>
          .
        </div>
      )}

      {errors?.base && (
        <div className="mb-4 rounded-md border border-destructive bg-surface px-4 py-3 text-[12px] text-destructive">
          {errors.base}
        </div>
      )}

      <form onSubmit={submit} className="flex flex-col gap-4">
        {steps.length === 0 && (
          <EmptyState
            title="No steps yet."
            description="Add a selection step to start building the funnel."
          />
        )}

        {steps.map((step, index) => (
          <div
            key={index}
            className="overflow-hidden rounded-md border border-hairline bg-surface"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline px-[18px] py-3">
              <div className="flex items-center gap-3">
                <span className="text-[13px] tabular-nums text-ink-muted">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <Badge variant={step.kind === 'engraving' ? 'tint' : 'ink'}>{step.kind}</Badge>
                <span className="text-[12px]">{step.label || step.title || 'Untitled step'}</span>
                {!step.required && <Badge variant="outline">Optional</Badge>}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="inline"
                  size="inline"
                  className="text-[10px]"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  Up
                </Button>
                <Button
                  type="button"
                  variant="inline"
                  size="inline"
                  className="text-[10px]"
                  disabled={index === steps.length - 1}
                  onClick={() => move(index, 1)}
                >
                  Down
                </Button>
                <Button
                  type="button"
                  variant="inline"
                  size="inline"
                  className="text-[10px] text-destructive"
                  onClick={() => setSteps((c) => c.filter((_, i) => i !== index))}
                >
                  Delete
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 p-[18px] max-[919px]:grid-cols-1">
              <TextRow
                label="Heading"
                hint="Shown at the top of the step"
                value={step.title}
                onChange={(v) => patch(index, { title: v })}
              />
              <TextRow
                label="Heading (RU)"
                value={step.titleRu}
                onChange={(v) => patch(index, { titleRu: v })}
              />
              <TextRow
                label="Rail label"
                hint="The short marker in the progress bar"
                value={step.label}
                onChange={(v) => patch(index, { label: v })}
              />
              <TextRow
                label="Rail label (RU)"
                value={step.labelRu}
                onChange={(v) => patch(index, { labelRu: v })}
              />
              <TextRow
                label="Hint"
                value={step.hint}
                onChange={(v) => patch(index, { hint: v })}
              />
              <TextRow
                label="Hint (RU)"
                value={step.hintRu}
                onChange={(v) => patch(index, { hintRu: v })}
              />

              <TextRow
                label="Key"
                hint="Written onto orders — changing it rewrites how past orders read"
                value={step.key}
                placeholder={slugify(step.label || step.title)}
                onChange={(v) => patch(index, { key: slugify(v) })}
              />

              <div className="sss-admin-field">
                <label className="sss-admin-label">Required</label>
                <select
                  className="sss-admin-select"
                  value={step.required ? 'true' : 'false'}
                  onChange={(e) => patch(index, { required: e.target.value === 'true' })}
                >
                  <option value="true">Required</option>
                  <option value="false">Optional</option>
                </select>
              </div>

              {step.kind === 'engraving' && (
                <>
                  <TextRow
                    label="Character limit"
                    type="number"
                    value={step.charLimit}
                    onChange={(v) => patch(index, { charLimit: v })}
                  />
                  <TextRow
                    label="Placeholder"
                    value={step.placeholder}
                    onChange={(v) => patch(index, { placeholder: v })}
                  />
                </>
              )}

              <ImageRow
                label="Step image"
                hint="Shown behind this step in the configurator"
                imageUrl={step.removeImage ? null : step.imageUrl}
                file={step.imageFile}
                onPick={(file) => patch(index, { imageFile: file, removeImage: false })}
                onClear={() => patch(index, { imageFile: undefined, removeImage: true })}
              />
            </div>

            {step.kind === 'selection' && (
              <div className="border-t border-hairline px-[18px] py-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-[13px] font-medium text-ink-muted">
                    Options
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="compact"
                    onClick={() =>
                      patch(index, { options: [...step.options, blankOption()] })
                    }
                  >
                    Add option
                  </Button>
                </div>

                <div className="flex flex-col gap-3">
                  {step.options.map((option, optionIndex) => (
                    <div
                      key={optionIndex}
                      className="grid grid-cols-2 gap-3 rounded-md border border-hairline p-3 max-[919px]:grid-cols-1"
                    >
                      <TextRow
                        label="Label"
                        value={option.label}
                        onChange={(v) => patchOption(index, optionIndex, { label: v })}
                      />
                      <TextRow
                        label="Label (RU)"
                        value={option.labelRu}
                        onChange={(v) => patchOption(index, optionIndex, { labelRu: v })}
                      />
                      <TextRow
                        label="Description"
                        value={option.description}
                        onChange={(v) => patchOption(index, optionIndex, { description: v })}
                      />
                      <TextRow
                        label="Description (RU)"
                        value={option.descriptionRu}
                        onChange={(v) => patchOption(index, optionIndex, { descriptionRu: v })}
                      />
                      <TextRow
                        label="Price bump"
                        hint="Added to the base price. 0 shows as “Included”."
                        type="number"
                        value={option.priceDelta}
                        onChange={(v) => patchOption(index, optionIndex, { priceDelta: v })}
                      />
                      <TextRow
                        label="Key"
                        value={option.key}
                        placeholder={slugify(option.label)}
                        onChange={(v) => patchOption(index, optionIndex, { key: slugify(v) })}
                      />

                      <div className="sss-admin-field">
                        <label className="sss-admin-label">Availability</label>
                        <select
                          className="sss-admin-select"
                          value={option.available ? 'true' : 'false'}
                          onChange={(e) =>
                            patchOption(index, optionIndex, {
                              available: e.target.value === 'true',
                            })
                          }
                        >
                          <option value="true">Available</option>
                          <option value="false">Out of stock</option>
                        </select>
                      </div>

                      <ImageRow
                        label="Option image"
                        hint="Swaps the preview when chosen"
                        imageUrl={option.removeImage ? null : option.imageUrl}
                        file={option.imageFile}
                        onPick={(file) =>
                          patchOption(index, optionIndex, { imageFile: file, removeImage: false })
                        }
                        onClear={() =>
                          patchOption(index, optionIndex, { imageFile: undefined, removeImage: true })
                        }
                      />

                      <div className="col-span-2 flex justify-end max-[919px]:col-span-1">
                        <Button
                          type="button"
                          variant="inline"
                          size="inline"
                          className="text-[10px] text-destructive"
                          onClick={() =>
                            patch(index, {
                              options: step.options.filter((_, j) => j !== optionIndex),
                            })
                          }
                        >
                          Remove option
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            variant="outline"
            size="compact"
            onClick={() => setSteps((c) => [...c, blankStep('selection')])}
          >
            Add selection step
          </Button>
          <Button
            type="button"
            variant="outline"
            size="compact"
            onClick={() => setSteps((c) => [...c, blankStep('engraving')])}
          >
            Add engraving step
          </Button>
          <Button type="submit" variant="primary" size="compact" processing={saving}>
            Save configurator
          </Button>
        </div>
      </form>
    </AdminLayout>
  )
}

function TextRow({
  label,
  hint,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string
  hint?: string
  value: string
  onChange: (value: string) => void
  type?: string
  placeholder?: string
}) {
  return (
    <div className="sss-admin-field">
      <label className="sss-admin-label">{label}</label>
      <input
        type={type}
        step={type === 'number' ? '0.01' : undefined}
        className="sss-admin-input"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <p className="sss-admin-hint">{hint}</p>}
    </div>
  )
}

function ImageRow({
  label,
  hint,
  imageUrl,
  file,
  onPick,
  onClear,
}: {
  label: string
  hint?: string
  imageUrl: string | null
  file?: File
  onPick: (file: File) => void
  onClear: () => void
}) {
  const preview = file ? URL.createObjectURL(file) : imageUrl

  return (
    <div className="sss-admin-field">
      <label className="sss-admin-label">{label}</label>
      <div className="flex items-center gap-3">
        {preview && (
          <img
            src={preview}
            alt=""
            className="size-12 shrink-0 rounded-sm border border-hairline object-cover"
          />
        )}
        <input
          type="file"
          accept="image/*"
          className="text-[11px] text-ink-muted"
          onChange={(e) => {
            const picked = e.target.files?.[0]
            if (picked) onPick(picked)
          }}
        />
        {preview && (
          <Button
            type="button"
            variant="inline"
            size="inline"
            className="text-[10px] text-ink-muted"
            onClick={onClear}
          >
            Clear
          </Button>
        )}
      </div>
      {hint && <p className="sss-admin-hint">{hint}</p>}
    </div>
  )
}
